import { FC, useState } from 'react'

// Components
import Select from 'components/molecules/Select/Select'
import Button from 'components/molecules/Button/Button'
import TextInput from 'components/molecules/TextInput/TextInput'
import PageDisplay from 'components/PageDisplay/PageDisplay'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import AdapterDayjs from '@mui/lab/AdapterDayjs'
import AutocompleteInput from 'components/organisms/AutocompleteInput/AutocompleteInput'
import { FormControl, IconButton, Typography, Box } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import FileUploadIcon from '@mui/icons-material/FileUpload'

// Utils
import dayjs from 'lib/dayjs'

// Logic
import useAddProcedureLogic from './AddProcedure.logic'

// Hook form
import { Controller } from 'react-hook-form'

// Style
import './AddProcedure.scss'
import AdmitNewPatient from './AdmitNewPatient/AdmitNewPatient'
import ImportSchedule from './ImportSchedule/ImportSchedule'

const AddProcedure: FC = () => {
  const {
    errors,
    control,
    patients,
    surgeons,
    isLoading,
    locations,
    procedures,
    isLoadingPatients,
    isLoadingSurgeons,
    isLoadingProcedures,
    isLoadingCreateSurgery,
    patientModalId,
    watch,
    handleAddPatient,
    onSubmit,
    handleSubmit,
    handleNavigateHome,
    isAdmitPatientModalOpen,
    setIsAdmitPatientModalOpen,
  } = useAddProcedureLogic()

  const [isImportModalOpen, setIsImportModalOpen] = useState(false)

  return (
    <PageDisplay>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="add-procedure-container">
          <div className="header">
            <IconButton onClick={handleNavigateHome}>
              <KeyboardBackspaceIcon className="back-icon" color="primary" />
            </IconButton>
            <Typography variant="h2">Manually Add Case/ Procedure</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<FileUploadIcon />}
              className="import-button"
              onClick={() => setIsImportModalOpen(true)}
            >
              Import Schedule
            </Button>
          </div>

          <Typography variant="h3">Patient Information</Typography>
          <div className="section">
            <Controller
              name="patient"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Patient name cannot be empty',
                },
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <div className="row">
                  <AutocompleteInput
                    {...field}
                    disabled={isLoadingCreateSurgery}
                    required={false}
                    variant="outlined"
                    handleChangeFormData={onChange}
                    placeholder="Patient Name"
                    label="Patient Name*"
                    options={patients}
                    error={!!errors.patient}
                    isLoading={isLoadingPatients}
                    helperText={errors.patient?.message}
                    shrink={true}
                    value={watch('patient')}
                  />
                  <Button
                    variant="contained"
                    size="large"
                    className="admit-button"
                    onClick={() => setIsAdmitPatientModalOpen(true)}
                  >
                    Add Case/ Procedure
                  </Button>
                </div>
              )}
            />

            <Controller
              name="patientDob"
              control={control}
              defaultValue={null}
              render={({ field: { onChange, value, ...field } }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    readOnly
                    openTo="month"
                    disabled
                    value={value ? dayjs(value) : null}
                    onChange={(newValue: dayjs.Dayjs | null) => {
                      onChange(newValue ? newValue.toDate() : null)
                    }}
                    slotProps={{
                      textField: {
                        label: 'Patient DOB',
                        error: !!errors.patientDob,
                        helperText: errors.patientDob?.message,
                        variant: 'outlined',
                        fullWidth: true,
                      },
                    }}
                    format="MM/DD/YYYY"
                    {...field}
                  />
                </LocalizationProvider>
              )}
            />

            <Controller
              name="mrNumber"
              control={control}
              defaultValue={null}
              render={({ field: { onChange, value, ...field } }) => (
                <TextInput
                  {...field}
                  disabled
                  value={value}
                  handleChangeFormData={onChange}
                  variant="outlined"
                  placeholder="MR#"
                  label="MR#"
                  error={!!errors.mrNumber}
                  helperText={errors.mrNumber?.message}
                  shrink
                />
              )}
            />
          </div>

          <Typography variant="h3">Case Information</Typography>
          <div className="section">
            <Controller
              name="surgeon"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Surgeon name cannot be empty',
                },
              }}
              render={({ field: { onChange, ...field } }) => (
                <AutocompleteInput
                  {...field}
                  required={false}
                  variant="outlined"
                  handleChangeFormData={onChange}
                  label="Surgeon Name*"
                  placeholder="Surgeon Name"
                  options={surgeons}
                  error={!!errors.surgeon}
                  helperText={errors.surgeon?.message as string}
                  isLoading={isLoadingSurgeons}
                  disabled={isLoadingCreateSurgery}
                />
              )}
            />

            <Controller
              name="procedure"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Procedure name cannot be empty',
                },
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <AutocompleteInput
                  {...field}
                  handleChangeFormData={onChange}
                  label="Procedure Name*"
                  placeholder="Procedure Name"
                  options={procedures}
                  error={!!errors.procedure}
                  helperText={errors.procedure?.message as string}
                  isLoading={isLoadingProcedures}
                  disabled={isLoadingCreateSurgery}
                  freeSolo
                  variant="outlined"
                  required={false}
                  sx={{ marginTop: '0px', marginBottom: '0px' }}
                />
              )}
            />

            <Controller
              name="procedureLocation"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Procedure location cannot be empty',
                },
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <Select
                  {...field}
                  onSelect={onChange}
                  label="Procedure Location*"
                  placeholder="Procedure Location"
                  options={locations}
                  isLoading={isLoading}
                  error={!!errors.procedureLocation}
                  helperText={errors.procedureLocation?.message as string}
                  disabled={isLoadingCreateSurgery}
                />
              )}
            />

            <Controller
              name="dateOfProcedure"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Procedure date cannot be empty',
                },
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: '10px',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    width: '100%',
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={value ? dayjs(value) : null}
                      onChange={(newValue: dayjs.Dayjs | null) => {
                        onChange(newValue ? newValue.toDate() : null)
                      }}
                      disabled={isLoadingCreateSurgery}
                      slotProps={{
                        textField: {
                          label: 'Date of Procedure*',
                          error: !!errors.dateOfProcedure,
                          helperText: errors.dateOfProcedure?.message,
                          variant: 'outlined',
                          fullWidth: true,
                          InputLabelProps: {
                            shrink: true,
                          },
                        },
                      }}
                      format="MM/DD/YYYY"
                      {...field}
                    />
                  </LocalizationProvider>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '10px',
                      justifyContent: { xs: 'space-between', sm: 'flex-start' },
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const today = dayjs().startOf('day')
                        onChange(today.toDate())
                        field.onBlur()
                      }}
                      disabled={isLoadingCreateSurgery}
                      sx={{
                        flex: { xs: 1, sm: 'none' },
                        minWidth: '80px',
                        height: '40px',
                      }}
                    >
                      Today
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const tomorrow = dayjs().add(1, 'day').startOf('day')
                        onChange(tomorrow.toDate())
                        field.onBlur()
                      }}
                      disabled={isLoadingCreateSurgery}
                      sx={{
                        flex: { xs: 1, sm: 'none' },
                        minWidth: '80px',
                        height: '40px',
                      }}
                    >
                      Tomorrow
                    </Button>
                  </Box>
                </Box>
              )}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="timeOfProcedure"
                control={control}
                defaultValue={null}
                rules={{
                  required: {
                    value: true,
                    message: 'Time of procedure cannot be empty',
                  },
                }}
                render={({ field }) => {
                  const { onChange, value, ref, ...rest } = field
                  const valueAsDayjs = value ? dayjs(value) : null

                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: '10px',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        width: '100%',
                      }}
                    >
                      <FormControl sx={{ width: '100%' }}>
                        <TimePicker
                          value={valueAsDayjs}
                          disabled={isLoadingCreateSurgery}
                          onChange={(newValue: dayjs.Dayjs | null) => {
                            onChange(newValue ? newValue.toDate() : null)
                          }}
                          slotProps={{
                            textField: {
                              label: 'Time of Procedure*',
                              error: !!errors.timeOfProcedure,
                              helperText: errors.timeOfProcedure?.message,
                              fullWidth: true,
                              InputLabelProps: {
                                shrink: true,
                              },
                              sx: {
                                '& .MuiInputBase-root': {
                                  color: '#333',
                                  backgroundColor: '#fff',
                                },
                                '& .Mui-focused': {
                                  color: '#1976d2',
                                },
                              },
                            },
                            popper: {
                              sx: {
                                '& .MuiPaper-root': {
                                  backgroundColor: '#fff',
                                  color: '#333',
                                },
                                '& .MuiPickersDay-root': {
                                  color: '#333',
                                },
                                '& .Mui-selected': {
                                  backgroundColor: '#1976d2 !important',
                                  color: '#fff !important',
                                },
                              },
                            },
                          }}
                          views={['hours', 'minutes']}
                          format="hh:mm a"
                          {...rest}
                        />
                      </FormControl>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => onChange(dayjs().toDate())}
                          disabled={isLoadingCreateSurgery}
                          sx={{
                            minWidth: { xs: '100%', sm: '80px' },
                            height: '40px',
                          }}
                        >
                          Now
                        </Button>
                      </Box>
                    </Box>
                  )
                }}
              />
            </LocalizationProvider>
          </div>

          <Typography variant="h3">Optional Information</Typography>
          <div className="section">
            <Controller
              name="operatingRoomNumber"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <TextInput
                  {...field}
                  shrink
                  value={value}
                  handleChangeFormData={onChange}
                  variant="outlined"
                  placeholder="Operating Room Number"
                  label="Operating Room Number"
                  type="number"
                  error={!!errors.operatingRoomNumber}
                  helperText={errors.operatingRoomNumber?.message}
                  disabled={isLoadingCreateSurgery}
                />
              )}
            />
          </div>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            isLoading={isLoadingCreateSurgery}
          >
            Add To Case Log
          </Button>
        </div>
      </form>
      <AdmitNewPatient
        key={patientModalId}
        open={isAdmitPatientModalOpen}
        onClose={() => setIsAdmitPatientModalOpen(false)}
        handleAdmittingNewPatient={handleAddPatient}
      />
      <ImportSchedule
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
      />
    </PageDisplay>
  )
}

export default AddProcedure
